import { useQuery } from "@tanstack/react-query";
import { api } from "../singleton/axios";

export interface EconomicCalendarEvent {
  date: string; // e.g., "2024-03-01 03:35:00"
  country: string; // e.g., "JP"
  event: string; // e.g., "3-Month Bill Auction"
  currency: string; // e.g., "JPY"
  previous: number | null;
  estimate: number | null;
  actual: number | null;
  change: number | null;
  impact: "Low" | "Medium" | "High" | "None";
  changePercentage: number | null;
  unit: string | null; // e.g., "K", "%", null
}

export interface EconomicCalendarResponse {
  data: {
    events: EconomicCalendarEvent[];
    groupedByDate: Record<string, EconomicCalendarEvent[]>;
    dateRange: {
      from: string; // YYYY-MM-DD
      to: string;   // YYYY-MM-DD
    };
    totalEvents: number;
  };
}

interface ApiError {
  success: false;
  error: string;
  message?: string;
  details?: string;
}

// Get economic calendar events for the current and next week
export const useEconomicCalendar = () => {
  return useQuery<EconomicCalendarResponse, ApiError>({
    queryKey: ["economicCalendar"],
    queryFn: async () => {
      const { data } = await api.get<EconomicCalendarResponse>("/economic-calendar");
      return data;
    },
    refetchOnWindowFocus: false, // Don't refetch on focus to avoid excessive requests
    // Actuals fill in through the day; the server caches this per-week for a
    // minute in Redis, so the poll costs at most one FMP call/min platform-wide.
    refetchInterval: 60 * 1000,
    staleTime: 1 * 60 * 1000, // 1 minute - ensure fresh economic event data
    gcTime: 5 * 60 * 1000, // 5 minutes - keep in cache briefly
  });
};

// Helper hook to get events for a specific date
export const useEconomicCalendarForDate = (dateString: string) => {
  const { data, ...rest } = useEconomicCalendar();
  
  const eventsForDate = data?.data.groupedByDate[dateString] || [];
  
  return {
    events: eventsForDate,
    isLoading: rest.isLoading,
    error: rest.error,
    refetch: rest.refetch,
  };
};

// Helper hook to get events by impact level
export const useEconomicCalendarByImpact = (impact?: "Low" | "Medium" | "High") => {
  const { data, ...rest } = useEconomicCalendar();
  
  const filteredEvents = impact 
    ? data?.data.events.filter(event => event.impact === impact) || []
    : data?.data.events || [];
  
  return {
    events: filteredEvents,
    isLoading: rest.isLoading,
    error: rest.error,
    refetch: rest.refetch,
  };
};

// Helper hook to get upcoming events (events from current time onwards)
export const useUpcomingEconomicEvents = () => {
  const { data, ...rest } = useEconomicCalendar();
  
  const now = new Date();
  const upcomingEvents = data?.data.events.filter(event => 
    new Date(event.date) >= now
  ) || [];
  
  // Sort by date (earliest first)
  upcomingEvents.sort((a, b) => 
    new Date(a.date).getTime() - new Date(b.date).getTime()
  );
  
  return {
    events: upcomingEvents,
    isLoading: rest.isLoading,
    error: rest.error,
    refetch: rest.refetch,
  };
};